import { useMemo, useState } from "react";
import { Segmented } from "antd";
import { currencySymbol } from "@src/util/currency/currency";
import { useDefaultCurrency } from "@src/hooks/useDefaultCurrency";

// Reports never add amounts in different currencies together. When a report's rows are in more
// than one currency, it shows one currency at a time: every row, subtotal and grand total on screen
// (and in its CSV) is then in that currency alone, and the switch moves between them.
//
// `sides` says how to read a row's currency: { pay: (row) => code } and/or { bill: (row) => code }.
// A report with both is split by the pay/bill pair, so its pay columns and its bill columns are
// each in one currency even when a duty is paid in one currency and billed in another.
// Rows with no currency recorded are in the tenant's default currency.
export function useReportCurrency(rows, sides) {
    const defaultCurrency = useDefaultCurrency();
    const [selected, setSelected] = useState(null);

    const keyOf = (row) => [
        sides.pay  ? (sides.pay(row)  || defaultCurrency) : "",
        sides.bill ? (sides.bill(row) || defaultCurrency) : "",
    ].join("|");

    const keys = useMemo(() => {
        const found = [...new Set((rows || []).map(keyOf))];
        // the default currency first, then alphabetical
        const isDefault = (key) => key.split("|").filter(Boolean).every((code) => code === defaultCurrency);
        return found.sort((a, b) => (isDefault(a) ? -1 : isDefault(b) ? 1 : a.localeCompare(b)));
    }, [rows, defaultCurrency]);

    const key = keys.includes(selected) ? selected : (keys[0] || [sides.pay ? defaultCurrency : "", sides.bill ? defaultCurrency : ""].join("|"));
    const [payCurrency, billCurrency] = key.split("|");

    const rowsInCurrency = useMemo(
        () => (keys.length <= 1 ? (rows || []) : (rows || []).filter((row) => keyOf(row) === key)),
        [rows, key, keys]
    );

    const labelOf = (k) => {
        const [pay, bill] = k.split("|");
        if (pay && bill && pay !== bill) return `Pay ${pay} · Bill ${bill}`;
        return pay || bill;
    };

    return {
        rows: rowsInCurrency,
        options: keys.map((k) => ({ value: k, label: labelOf(k) })),
        selected: key,
        setSelected,
        payCurrency: payCurrency || defaultCurrency,
        billCurrency: billCurrency || defaultCurrency,
        paySymbol: currencySymbol(payCurrency || defaultCurrency),
        billSymbol: currencySymbol(billCurrency || defaultCurrency),
        // margin/profit only means something when pay and bill are in the same currency
        sameCurrency: !payCurrency || !billCurrency || payCurrency === billCurrency,
    };
}

// Only shown when the report's rows are in more than one currency.
export const ReportCurrencySwitch = ({ reportCurrency }) => {
    const { options, selected, setSelected } = reportCurrency;
    if (options.length <= 1) return null;
    return (
        <div className="report-currency-switch" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", margin: "8px 0 12px" }}>
            <span style={{ fontSize: 13, color: "#374151" }}>
                This report has amounts in {options.length} currencies, which are never added together. Showing:
            </span>
            <Segmented value={selected} onChange={setSelected} options={options} />
        </div>
    );
};
